/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./Canvas',
	'../features/content/InlineFeatures'
], (CanvasImpl, InlineFeatures) => {
	class CanvasFactory {


		constructor(options){
			this.dashboardAPI = options.dashboardAPI;
			this.logger = options.logger;
			this.featureLoader = options.featureLoader;
			this.inlineFeature = options.inlineFeature || InlineFeatures;
			this.extensions = options.extensions;
			this.boardModel = options.boardModel;
			this.eventRouter = options.eventRouter;
			this.widgetRegistry  =  options.widgetRegistry;
			this.profile = this.dashboardAPI.getAppConfig('profile') || null;
			this._initialContentSelection = options.settings.get('applicationSettings').subView;
		}

		createCanvas(){
			const options = {
				// Features required by the canvas and the content API
				dashboardFeatures: this.featureLoader,
				boardModel: this.boardModel,
				extensions: this.extensions,
				// the following 2 should be cleaned up
				copyCallbabck: this.dashboardAPI.copy.bind(this.dashboardAPI),
				pasteCallback: this.dashboardAPI.paste.bind(this.dashboardAPI),
				profile: this.profile
			};

			const canvasImpl = new CanvasImpl(options);

			const internalCanvas = {
				initializeContentFeatures: () => {
					return this.dashboardAPI.findGlassCollection('com.ibm.bi.dashboard.content-features').then((collectionItems) => {
						let contentFeatureCollection = this._filterFeatures(this.inlineFeature.concat(collectionItems || []));
						const contentTypeRegistry = this.dashboardAPI.getFeature('ContentTypeRegistry');
						if (contentTypeRegistry) {
							contentFeatureCollection = contentTypeRegistry.processFeatureCollection(contentFeatureCollection);
						}
						return canvasImpl.initializeContentFeatures(contentFeatureCollection);
					});
				},
				whenContentReady: (contentId) => {
					return canvasImpl.whenContentReady(contentId);
				},
				registerFeatureCollection: (contentId, collectionName) => {
					return this.dashboardAPI.findGlassCollection(collectionName).then((collectionItems) => {
						return canvasImpl.registerContentFeatures(contentId, collectionItems || []);
					});
				},
				registerFeature: (contentId, featureId, featureInstance, featureSpec) => {
					return canvasImpl.registerContentFeature(contentId, featureId, featureInstance, featureSpec);
				},
				registerDeprecatedFeature: (contentId, featureId, featureInstance, featureSpec) => {
					return canvasImpl.registerContentFeature(contentId, featureId, featureInstance, featureSpec, true);
				},
				getContentInstance: (contentId) => {
					return canvasImpl.getContentInstance(contentId);
				},
				destroy: () => {
					canvasImpl.destroy();
				}
			};

			// now the canvas is ready, dashboard spec is available via the 'Serializer' feature, no need of the initial one
			this.boardModel.deleteInitialSpec();
			this._selectTab(canvasImpl);
			return {
				canvasImpl: canvasImpl,
				internalCanvas: internalCanvas
			};
		}

		/**
		 * Select the initial content with the given initial selection (subView param)
		 * If the initial selection is not defined, simply select the first top level layout.
		 * @param {CanvasImpl} canvas
		 */
		_selectTab(canvas) {
			if (this._initialContentSelection && canvas.getContent(this._initialContentSelection)) {
				canvas.selectContent([ this._initialContentSelection ]);
			} else if (this.boardModel.layout.items && this.boardModel.layout.items.length) {
				canvas.selectContent([ this.boardModel.layout.items[0].id ]);
			}
		}

		_filterFeatures(collectionItems) {
			const featureChecker = this.dashboardAPI && this.dashboardAPI.getGlassCoreSvc('.FeatureChecker');
			const isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');
			return collectionItems.filter((feature) => {
				return (
					isPropertiesV2Enabled && feature.id !== 'com.ibm.bi.dashboard.content-features.properties'
					|| !isPropertiesV2Enabled && feature.id !== 'com.ibm.bi.dashboard.content-features.propertiesV2'
				);
			});
		}

	}

	return CanvasFactory;
});
